import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import Button from "../../inputs/button";
import Radio from "../../inputs/radio";
import { getActivationMethods } from "../../../services/cohort";
import * as CONST from "../../../utilities/constants";
import { isEquativMethodId, isValidEquativPrice, getEquativPriceFromMethods } from "../../../utilities/equativPrice";
import searchIcon from "../../../assets/svgs/search.svg";
import trashIcon from "../../../assets/svgs/trash.svg";
import xGreenIcon from "../../../assets/svgs/x-green.svg";
import "./DestinationsModal.scss";

// GAM network selection options
const gamNetworkOptions = [
	{ value: "all", label: "All Networks" },
	{ value: "subset", label: "Select Specific Networks" },
];

/**
 * DestinationsModal - Dual-pane modal for selecting push destinations
 *
 * Layout:
 * - Left pane: Searchable checkbox list of available destinations
 * - Right pane: Selected destinations with delete buttons
 */
function DestinationsModal({
	isOpen,
	onClose,
	onConfirm,
	initialSelected = [],
	loading = false,
	title = "Select Destinations",
	saveButtonText = "Save & Push",
	gamNetworkCodes = [],
	initialGamNetworkMode = "all",
	initialGamNetworkSubset = [],
	allowEquativPricePrompt = true,
}) {
	const [destinations, setDestinations] = useState([]);
	const [selected, setSelected] = useState(initialSelected);
	const [searchQuery, setSearchQuery] = useState("");
	const [loadingDestinations, setLoadingDestinations] = useState(true);

	// GAM network selection state
	const [gamNetworkMode, setGamNetworkMode] = useState(initialGamNetworkMode);
	const [selectedNetworkCodes, setSelectedNetworkCodes] = useState(initialGamNetworkSubset);

	// Equativ price prompt state (ASB-3872, edit-path prefill added ASB-4022) — shown any
	// time Equativ is selected, prefilled from the audience's current price on edit.
	// initialEquativPrice is the prefilled value itself, kept alongside the editable
	// equativPrice so handleConfirm can tell a real edit from an untouched resend (below).
	const [equativPrice, setEquativPrice] = useState("");
	const [initialEquativPrice, setInitialEquativPrice] = useState("");

	// Fetch activation methods on mount
	useEffect(() => {
		const fetchDestinations = async () => {
			try {
				setLoadingDestinations(true);
				const methods = await getActivationMethods();
				const formatted = methods.map((el) => ({
					id: el.id,
					name: el.name,
					type: el.type,
					label:
						el.type === "prebid"
							? `${el.name} (${CONST.activationMethodTypeMap.find((t) => t.value === el.type)?.label ?? el.type})`
							: el.name,
				}));
				setDestinations(formatted);
			} catch (error) {
				console.error("Failed to fetch activation methods:", error);
			} finally {
				setLoadingDestinations(false);
			}
		};

		if (isOpen) {
			fetchDestinations();
		}
	}, [isOpen]);

	// Initialize selected from initialSelected when destinations load
	useEffect(() => {
		if (initialSelected.length > 0 && destinations.length > 0) {
			// Match initialSelected IDs to full destination objects
			const initialIds = initialSelected.map((d) => d.id || d);
			const matched = destinations.filter((d) => initialIds.includes(d.id));
			setSelected(matched);
		}
	}, [destinations, initialSelected]);

	// Sync GAM network + Equativ price prompt state on the isOpen false->true transition only —
	// NOT on every render while already open. initialSelected/initialGamNetworkSubset are new
	// array literals on every caller render in "create" mode (e.g. `initialSelected={mode ===
	// "create" ? [] : activationMethods}`), so keying this effect on those values directly (as it
	// used to) re-fires on any unrelated re-render of the modal's parent while the modal sits
	// open, silently resetting GAM selections and wiping an in-progress typed Equativ price.
	const wasOpenRef = useRef(false);
	useEffect(() => {
		if (isOpen && !wasOpenRef.current) {
			setGamNetworkMode(initialGamNetworkMode);
			setSelectedNetworkCodes(initialGamNetworkSubset);
			// Prefill from the audience's current price (details.equativ.price, ASB-3622 shape)
			// when Equativ is already active; "" (use the default) for a genuinely new activation.
			const prefillPrice = getEquativPriceFromMethods(initialSelected);
			setEquativPrice(prefillPrice);
			setInitialEquativPrice(prefillPrice);
		}
		wasOpenRef.current = isOpen;
	});

	// Filter destinations by search query
	const filteredDestinations = destinations.filter((d) =>
		d.label.toLowerCase().includes(searchQuery.toLowerCase())
	);

	// Check if a destination is selected
	const isSelected = (destination) => {
		return selected.some((s) => s.id === destination.id);
	};

	// Toggle destination selection
	const toggleSelection = (destination) => {
		if (isSelected(destination)) {
			setSelected(selected.filter((s) => s.id !== destination.id));
		} else {
			setSelected([...selected, destination]);
		}
	};

	// Remove from selection
	const removeSelection = (destination) => {
		setSelected(selected.filter((s) => s.id !== destination.id));
	};

	// Check if GAM is selected (GAM has id=1)
	const isGamSelected = selected.some((d) => d.id === 1 || d.id === "1");

	const isEquativSelected = selected.some((d) => isEquativMethodId(d.id));
	const showEquativPricePrompt = allowEquativPricePrompt && isEquativSelected;
	// Gated on showEquativPricePrompt: without it, stale invalid text left over from when the
	// field was visible would keep disabling Confirm even after Equativ is deselected and the
	// field itself disappears (ASB-3872 review).
	const equativPriceInvalid = showEquativPricePrompt && equativPrice !== "" && !isValidEquativPrice(equativPrice);

	// Handle confirm
	const handleConfirm = () => {
		// Only send a price when it actually changed from what was prefilled — an edit that
		// leaves it untouched must not re-post the same value (could clobber a price someone
		// else just set via the Manager's column in the meantime), and clearing a prefilled
		// price back to blank is "leave it unchanged", not "reset to the default" (ASB-4022
		// review; see the help text below).
		const priceChanged = equativPrice !== initialEquativPrice;
		const priceToSend =
			showEquativPricePrompt && priceChanged && equativPrice !== "" ? Number(equativPrice) : undefined;
		onConfirm(selected, gamNetworkMode, selectedNetworkCodes, priceToSend);
	};

	if (!isOpen) return null;

	return (
		<div className="destinations-modal-overlay">
			<div className="destinations-modal">
				{/* Header */}
				<div className="modal-header">
					<h2>{title}</h2>
					<button type="button" className="close-btn" onClick={onClose}>
						<img src={xGreenIcon} alt="Close" />
					</button>
				</div>

				{/* Body - Dual Pane */}
				<div className="modal-body">
					{/* Left Pane - Available Destinations */}
					<div className="left-pane">
						<div className="search-wrapper">
							<img src={searchIcon} alt="Search" className="search-icon" />
							<input
								type="text"
								placeholder="Search"
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
								className="search-input"
							/>
						</div>
						<div className="destinations-list">
							{loadingDestinations ? (
								<div className="loading-text">Loading destinations...</div>
							) : filteredDestinations.length === 0 ? (
								<div className="empty-text">No destinations found</div>
							) : (
								filteredDestinations.map((destination) => (
									<label key={destination.id} className="destination-item">
										<input
											type="checkbox"
											checked={isSelected(destination)}
											onChange={() => toggleSelection(destination)}
										/>
										<span className="checkmark"></span>
										<span className="destination-label">{destination.label}</span>
									</label>
								))
							)}
						</div>
					</div>

					{/* Divider */}
					<div className="pane-divider" />

					{/* Right Pane - Selected Destinations */}
					<div className="right-pane">
						<div className="selected-header">
							Selected ({selected.length})
						</div>
						<div className="selected-list">
							{selected.length === 0 ? (
								<div className="empty-text">No destinations selected</div>
							) : (
								selected.map((destination) => (
									<div key={destination.id} className="selected-item">
										<span className="selected-label">{destination.label}</span>
										<button
											type="button"
											className="delete-btn"
											onClick={() => removeSelection(destination)}
										>
											<img src={trashIcon} alt="Remove" />
										</button>
									</div>
								))
							)}
						</div>

						{/* GAM Network Configuration */}
						{isGamSelected && gamNetworkCodes.length > 0 && (
							<div className="gam-network-section">
								<div className="gam-network-header">
									<span className="gam-section-label">GAM Network Selection</span>
								</div>
								<Radio
									options={gamNetworkOptions}
									name="gamNetworkMode"
									style="list"
									disabled={false}
									required={false}
									selected={gamNetworkOptions.find((opt) => opt.value === gamNetworkMode)}
									onInput={(opt) => setGamNetworkMode(opt.value)}
								/>
								{gamNetworkMode === "subset" && (
									<div className="network-codes-list">
										{gamNetworkCodes.map((code) => (
											<label key={code} className="network-code-item">
												<input
													type="checkbox"
													checked={selectedNetworkCodes.includes(code)}
													onChange={(e) => {
														if (e.target.checked) {
															setSelectedNetworkCodes([...selectedNetworkCodes, code]);
														} else {
															setSelectedNetworkCodes(selectedNetworkCodes.filter((c) => c !== code));
														}
													}}
												/>
												<span className="checkmark"></span>
												<span>{code}</span>
											</label>
										))}
									</div>
								)}
							</div>
						)}

						{/* Equativ Price Prompt */}
						{showEquativPricePrompt && (
							<div className="gam-network-section">
								<div className="gam-network-header">
									<span className="gam-section-label">Equativ Price (CPM)</span>
								</div>
								<div className="chips-info p5">
									{initialEquativPrice === ""
										? "Leave blank to use the default price. This can also be edited later from the Audience Manager's Equativ Price column."
										: "Clearing this field leaves the current price unchanged. This can also be edited from the Audience Manager's Equativ Price column."}
								</div>
								<input
									type="text"
									inputMode="decimal"
									placeholder="e.g. 2.50"
									value={equativPrice}
									onChange={(e) => setEquativPrice(e.target.value)}
									className="equativ-price-input"
								/>
								{equativPriceInvalid && (
									<div className="chips-info p5">
										Enter a non-negative number with up to 2 decimal places.
									</div>
								)}
							</div>
						)}
					</div>
				</div>

				{/* Footer */}
				<div className="modal-footer">
					<button
						type="button"
						className="cancel-link"
						onClick={onClose}
						disabled={loading}
					>
						Cancel
					</button>
					<Button
						text={saveButtonText}
						type="button"
						style="dark"
						onClick={handleConfirm}
						disabled={selected.length === 0 || loading || equativPriceInvalid}
						loaded={loading}
					/>
				</div>
			</div>
		</div>
	);
}

DestinationsModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	onConfirm: PropTypes.func.isRequired,
	initialSelected: PropTypes.array,
	loading: PropTypes.bool,
	title: PropTypes.string,
	saveButtonText: PropTypes.string,
	gamNetworkCodes: PropTypes.array,
	initialGamNetworkMode: PropTypes.oneOf(["all", "subset"]),
	initialGamNetworkSubset: PropTypes.array,
	allowEquativPricePrompt: PropTypes.bool,
};

DestinationsModal.defaultProps = {
	initialSelected: [],
	loading: false,
	title: "Select Destinations",
	saveButtonText: "Save & Push",
	gamNetworkCodes: [],
	initialGamNetworkMode: "all",
	initialGamNetworkSubset: [],
	allowEquativPricePrompt: true,
};

export default DestinationsModal;
